import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ILocaleNameLookup, LocaleAbbreviation } from '../locale-select/ILocaleNameLookup';
import { IOrganization } from '../models/organization/IOrganization';
import { IPractice } from '../models/practice/IPractice';
import { IAuthenticatedUser } from '../models/user-interfaces';
import { IHeaderLoading, IHeaderMenuOptions, IHeaderState, IHeaderVisibility } from './IHeaderState';

/** A standard header component that is intended to ensure each portion within is displayed in a consistent way. */
@Component({
    // tslint:disable-next-line:component-selector
    selector: 'ofi-foundation-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FoundationHeaderComponent implements OnChanges {
    /** Maybe we'll store the state/configuration here? */
    headerState = new BehaviorSubject<IHeaderState>({
        username: undefined,
        user: undefined,
        visibility: {
            selectPractice: true,
            selectOrganization: true,
            user: true,
            translate: false,
            logo: true,
            whiteLabelLogo: false,
            locale: true
        },
        organization: undefined,
        practice: undefined,
        value: {
            organization: undefined,
            practice: undefined,
            locale: 'en-US'
        },
        practices: [],
        organizations: [],
        userMenuItems: [
            { name: 'Account', url: 'https://orthofi.com/Account/MyAccount' },
            { name: 'Log Out', url: 'https://keymaster.orthofi.com/api/account/logout' }
        ],
        loading: {
            practices: false,
            organizations: false
        }
    });

    /** Emits the full practice object on selection. */
    @Output() practiceSelectionChanged = new EventEmitter<IPractice>();
    /** Emits the full practice object on selection. */
    @Output() organizationSelectionChanged = new EventEmitter<IOrganization>();
    /** Emits the full practice object on selection. */
    @Output() localeSelectionChanged = new EventEmitter<string>();

    /** The logged in visitor's information. */
    @Input() user: IAuthenticatedUser;
    /** An override for the name that will appear next to the user icon, which should be the user's first. But, that isn't available on authenticated user. */
    @Input() username: string;
    /** Which items should appear in the header. */
    @Input() visibility?: IHeaderVisibility;
    /** Experimental Details about the selected organization. Pre-populates the organization dropdown, overriding the selection made by checking the user org. */
    @Input() organization?: IOrganization;
    /** Experimental: The list of organizations to present in the organization select. */
    @Input() organizations?: Array<IOrganization> = [];
    /** Details about the current practice, which are used to brand the header and in practice selection. */
    @Input() practice?: IPractice;
    /** A list of practices to present in the practice select. */
    @Input() practices?: Array<IPractice> = [];
    /** The items that should appear in the menu when the user clicks their name. */
    @Input() userMenuItems?: IHeaderMenuOptions;
    /** Mark portions of the header UI as loading. */
    @Input() loading?: IHeaderLoading;
    /** A language to be selected by default */
    @Input() locale: LocaleAbbreviation;
    /** A list of languages that can be selected from. */
    @Input() locales: ILocaleNameLookup;

    /** @deprecated A callback which fires when the menu button on the left of the header is visible and activated. Use menuButtonActivated instead. */
    @Input() menuButtonCB?: Function = () => {};

    /** @ignore */
    constructor () {}

    /** @ignore */
    ngOnChanges (changes: SimpleChanges): void {
        // Get the current value of any changed prop.
        const updated: any = {};
        for (const propName in changes) {
            if (changes.hasOwnProperty(propName)) {
                updated[propName] = changes[propName].currentValue;
            }
        }

        this.headerState.next({
            ...this.headerState.value,
            ...updated
        });
    }

    /** Get the currently selected values from the header. */
    get value () {
        return this.headerState.value.value;
    }

    /** Save and emit the selected practice value. */
    onSelectPractice (practice) {
        const current = this.headerState.value;
        this.headerState.next({ ...current, value: { ...current.value, practice } });
        this.practiceSelectionChanged.emit(practice);
    }

    /** Save and emit the selected organization value. */
    onSelectOrganization (organization) {
        const current = this.headerState.value;
        this.headerState.next({ ...current, value: { ...current.value, organization } });
        this.organizationSelectionChanged.emit(organization);
    }

    /** Save and emit the selected locale value. */
    onSelectLocale (locale) {
        const current = this.headerState.value;
        this.headerState.next({ ...current, value: { ...current.value, locale } });
        this.localeSelectionChanged.emit(locale);
    }
}
